<template>
	<div class="guquan-wrap">
		<div class="chart-title">枣庄银行股权划转工作</div>
		<div class="chart-box" ref="chart"></div>

		<!-- 内联弹窗 -->
		<div
			class="guquan-popup dashboard-chart-popup dashboard-chart-popup--wide"
			v-if="popupVisible"
			v-append-to-body
		>
			<div class="popup-head">
				<div class="popup-dot"></div>
				<span class="popup-head-text">枣庄银行股权划转工作明细</span>
				<el-button @click="popupVisible = false" type="text" icon="el-icon-circle-close" />
			</div>
			<el-table :data="records" stripe :border="false"
			style="width:100%;background-color:#071a52;">
				<el-table-column type="index" width="45" label="序号" align="center" />
			<el-table-column prop="date" label="划转时间" width="95" align="center" />
			<el-table-column prop="transferShares" label="划转股份" width="100" align="center" />
			<el-table-column prop="holdRatio" label="持股比例" width="75" align="center" />
			<el-table-column prop="totalShares" label="合计股份" width="100" align="center" />
			<el-table-column prop="ratio" label="总计比例" width="75" align="center" />
			<el-table-column prop="remark" label="备注" width="85" align="center" />
			<el-table-column prop="price" label="转让价格" width="80" align="center" />
			<el-table-column prop="company" label="转出方" show-overflow-tooltip />
			</el-table>
		</div>
	</div>
</template>

<script>
	import * as echarts from 'echarts';
	import apis from '@/http/apis';
	import { mergeGovTooltip } from './gov-chart-tooltip';

	const BAR_COLORS = ['#0075ff', '#0fb36e', '#36cbcb', '#d7d228', '#ff7a45', '#9254de'];

	export default {
		name: 'ZaozhuangBankGuquanZhuanrang',
		data() {
			return {
				chart: null,
				popupVisible: false,
				records: [],
				barData: [],
			};
		},
		created() {
			this.fetchData();
		},
		mounted() {
			window.addEventListener('resize', this.handleResize);
		},
		beforeDestroy() {
			if (this.chart) this.chart.dispose();
			window.removeEventListener('resize', this.handleResize);
		},
		methods: {
			handleResize() {
				if (this.chart) this.chart.resize();
			},

			formatDisplayDate(dateStr) {
				if (!dateStr) return '';
				const parts = String(dateStr).split('-');
				if (parts.length !== 3) return dateStr;
				return `${parts[0]}.${Number(parts[1])}.${Number(parts[2])}`;
			},

			formatNumber(val) {
				if (val == null || val === '') return '';
				return String(Math.round(Number(val)));
			},

			formatPercent(val) {
				if (val == null || val === '') return '';
				return `${Number(val).toFixed(2).replace(/\.?0+$/, '')}%`;
			},

			formatPrice(val) {
				if (val == null || val === '') return '';
				return String(val);
			},

			mapDetailRecords(list) {
				return (list || []).map(item => ({
					date: this.formatDisplayDate(item.transferTime),
					transferShares: this.formatNumber(item.transferShares),
					holdRatio: this.formatPercent(item.shareholdingRatio),
					totalShares: this.formatNumber(item.totalShares),
					ratio: this.formatPercent(item.totalRatio),
					remark: item.description || '',
					price: this.formatPrice(item.transferPrice),
					company: item.transferOutParty || '',
				}));
			},

			mapBarData(barList) {
				return (barList || []).map((item, index) => ({
					name: item.name || '',
					value: Number(item.value) || 0,
					color: BAR_COLORS[index % BAR_COLORS.length],
					date: item.date || '',
					totalShares: this.formatNumber(item.totalShares),
				}));
			},

			fetchData() {
				apis.zzBankEquityTransferDetail()
					.then((res) => {
						const payload = res && res.data ? res.data : {};
						this.records = this.mapDetailRecords(payload.list);
						this.barData = this.mapBarData(payload.barList);
						this.initChart();
					})
					.catch((err) => {
						console.error('枣庄银行股权划转进度接口请求失败', err);
						this.records = [];
						this.barData = [];
						this.initChart();
					});
			},

			initChart() {
				if (this.chart) {
					this.chart.dispose();
					this.chart = null;
				}

				const barData = this.barData;
				this.chart = echarts.init(this.$refs.chart);

				const maxValue = barData.reduce((max, item) => Math.max(max, item.value), 0);
				const xMax = maxValue > 0 ? Math.ceil(maxValue * 1.15) : 16;

				const option = {
					grid: {
						left: '2%',
						right: '14%',
						top: '8%',
						bottom: '4%',
						containLabel: true,
					},
					tooltip: mergeGovTooltip({
						trigger: 'axis',
						axisPointer: { type: 'shadow' },
						backgroundColor: 'rgba(0,20,60,0.92)',
						borderColor: '#0075ff',
						borderWidth: 1,
						textStyle: { color: '#cce8ff', fontSize: 11 },
						formatter: params => {
							const d = barData[params[0].dataIndex];
							if (!d) return '';
							return `${d.name}<br/>总计比例：${d.value}%<br/>划转时间：${d.date}<br/>合计股份：${d.totalShares}`;
						},
					}),
					xAxis: {
						type: 'value',
						max: xMax,
						axisLabel: {
							color: '#8ab4d4',
							fontSize: 9,
							formatter: val => val + '%',
						},
						splitLine: { lineStyle: { color: 'rgba(255,255,255,0.08)' } },
						axisLine: { show: false },
					},
					yAxis: {
						type: 'category',
						data: barData.map(d => d.name),
						inverse: true,
						axisLabel: {
							color: '#cce8ff',
							fontSize: 10,
							interval: 0,
						},
						axisLine: { lineStyle: { color: '#1a4a8a' } },
						axisTick: { show: false },
					},
					series: [{
						type: 'bar',
						barWidth: '50%',
						data: barData.map(d => ({
							value: d.value,
							itemStyle: {
								color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
									{ offset: 0, color: d.color + '88' },
									{ offset: 1, color: d.color },
								]),
								borderRadius: [0, 4, 4, 0],
							},
						})),
						label: {
							show: true,
							position: 'right',
							color: '#fcf883',
							fontSize: 11,
							fontWeight: 'bold',
							formatter: params => barData[params.dataIndex].value + '%',
						},
					}],
				};
				this.chart.setOption(option);
				this.chart.off('click');
				this.chart.on('click', () => {
					this.popupVisible = true;
				});
			},
		},
	};
</script>

<style scoped>
	.guquan-wrap {
		width: 100%;
		height: 100%;
		display: flex;
		flex-direction: column;
		box-sizing: border-box;
		overflow: visible;
	}

	.chart-title {
		font-size: 12px;
		color: #fcf883;
		text-align: center;
		padding: 3px 0 0;
		letter-spacing: 1px;
		flex-shrink: 0;
	}

	.chart-box {
		flex: 1;
		min-height: 0;
		overflow: visible;
	}

	/* 弹窗定位见 chart-popup.scss（dashboard-chart-popup） */
	.guquan-popup {
		width: max-content !important;
		max-width: 92vw;
		left: 50% !important;
		transform: translateX(-50%);
	}

	.popup-head {
		margin: 10px;
		color: #ffffff;
		font-size: 14px;
		font-weight: 600;
		display: flex;
		align-items: center;
		gap: 5px;
		position: relative;
	}

	.popup-dot {
		width: 14px;
		height: 14px;
		border-radius: 50%;
		background-color: #fcf883;
		flex-shrink: 0;
	}

	.popup-head-text {
		flex: 1;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.guquan-popup ::v-deep .el-icon-circle-close {
		position: absolute;
		right: 0;
		top: 50%;
		transform: translateY(-50%);
		font-size: 20px;
		color: #fff;
	}

	.guquan-popup ::v-deep .el-table tr {
		color: #fff !important;
		background-color: #071a52 !important;
	}

	.guquan-popup ::v-deep .el-table th.el-table__cell {
		color: #fff !important;
		background-color: #3c4a77 !important;
	}

	.guquan-popup ::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
		background-color: #0d2569 !important;
	}

	.guquan-popup ::v-deep .el-table__cell {
		padding: 5px 0 !important;
		font-size: 12px;
		border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
		border-bottom: none !important;
	}

	.guquan-popup ::v-deep .el-table::before {
		height: 0;
	}
</style>
